import { Box, Button, Divider, useTheme } from '@mui/material';
import { useTranslation } from 'react-i18next';
import { useForm, FormProvider } from 'react-hook-form';
import * as yup from 'yup';
import { useQueryClient } from 'react-query';
import { yupResolver } from '@hookform/resolvers/yup';

import { ControlledTextField } from '@frontend/features/common/components/ControlledTextField';
import { client } from '@frontend/features/common/services';
import { useSnackBar } from '@frontend/features/common/components/SnackBarContext';
import { useState } from 'react';
import { LoadingButton } from '@mui/lab';

interface FormProps {
  onClose: () => void;
  agencyId: string;
}

interface CreateTeamDTO {
  name: string;
  color: string;
}

const resolver = yupResolver(
  yup.object().shape({
    name: yup.string().required(),
    color: yup
      .string()
      .test((value) => value !== 'no-selection')
      .required(),
  }),
);

export const Form = ({ onClose, agencyId }: FormProps) => {
  const theme = useTheme();
  const { t } = useTranslation();
  const { addToast } = useSnackBar();
  const [loading, setLoading] = useState(false);
  const queryClient = useQueryClient();

  const methods = useForm({
    resolver,
  });
  const { handleSubmit, control } = methods;

  const onSubmit = async (data: CreateTeamDTO) => {
    setLoading(true);
    try {
      await client.post('/team', { ...data, agencyId });
      addToast({
        message: t('teams.create.createdSuccess'),
        color: 'success',
      });
    } catch (e) {
      addToast({
        message: t('teams.create.createdError'),
        color: 'error',
      });

      // TODO: esto tendria q ser un sentry emit;
      console.log(e);
    } finally {
      setLoading(false)
      queryClient.invalidateQueries();
      onClose();
    }
  };

  return (
    <FormProvider {...methods}>
      <form onSubmit={handleSubmit(onSubmit)}>
        <Box
          display="grid"
          gridTemplateColumns="repeat(12, 1fr)"
          gap={2}
          sx={{
            maxHeight: 'calc(100vh - 300px)',
            overflowX: 'auto',
            paddingTop: theme.spacing(2),
          }}
        >
          <Box gridColumn="span 12">
            <ControlledTextField
              control={control}
              name="name"
              label={t('teams.create.name')}
              placeholder={t('common.form.onlyLetters')}
              required
              errorMessage={t('common.form.required')}
              fullWidth
            />
          </Box>
          <Box gridColumn="span 10">
            <ControlledTextField
              control={control}
              name="color"
              label={t('teams.create.color')}
              placeholder={t('common.form.onlyLetters')}
              required
              errorMessage={t('common.form.required')}
              fullWidth
            />
          </Box>

          <Box gridColumn="span 2">
            <ControlledTextField
              control={control}
              name="color"
              type="color"
              InputLabelProps={{ shrink: true }}
              fullWidth
            />
          </Box>
        </Box>
        <Box display="flex" flexDirection="column" gap={theme.spacing(3)} paddingTop={theme.spacing(3)}>
          <Divider />
          <Box
            display="flex"
            justifyContent="space-evenly"
            width="100%"
            gap={theme.spacing(1)}
            sx={{
              '& > button': {
                width: 'calc(50% - 4px)',
              },
            }}
          >
            <Button variant="outlined" color="error" size="medium" onClick={onClose}>
              {t('common.form.discard')}
            </Button>
            <LoadingButton loading={loading} disabled={loading} variant="contained" color="primary" type="submit" size="medium">
              {t('common.form.save')}
            </LoadingButton>
          </Box>
        </Box>
      </form>
    </FormProvider>
  );
};
